/**
 * The Activity row of a cell's last pass (CU-869fn0gcp v1.4), so Ask Iris on a cell can send Activity's own
 * "How this was calculated" (`derivationEvidence`). `/activity` has no read by id: the cell × pass is asked for with
 * Activity's list (`activityActions.listDecisions`, which applies `toNumbers` — `simulated` comes from it) and the
 * row is chosen here. Two faces of one site in one cell share an `event_id` (legacy.md), so the row must also carry
 * the price `/pushes` chose; none or several → null, never the first row. Pure and alias-free: the list is injected.
 */
import { cellParams } from '../../Activity/@services/cellFilter.js'

const cents = v => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Math.round(Number(v) * 100))

/** The server's filters for one pass × office × ACRISS × pick-up × length, or null without a pass or an event. */
export function decisionQuery(push, data) {
	if (!push?.pass || !push?.event_id) return null
	const d = push.pickup_date
	const lor = data?.sample?.lor ?? null
	return {
		execution_id: push.pass,
		office: data?.sample?.office || null,
		sipp: push.sipp || null,
		...cellParams({ pickup_from: d, pickup_to: d, duration_from: lor, duration_to: lor }),
	}
}

/** The one row with `id == event_id` and `proposed_price == target` (to the cent), else null. */
export function matchDecision(rows, push) {
	const target = cents(push?.target)
	if (!push?.event_id || target == null) return null
	const hits = (rows || []).filter(r => String(r?.id) === String(push.event_id) && cents(r?.proposed_price) === target)
	return hits.length === 1 ? hits[0] : null
}

/**
 * Read and match the row of `push`. `list` is `activityActions.listDecisions`. Resolves null when there is nothing to
 * ask, the read fails, or `signal` was aborted meanwhile (the dialog closed or moved to another cell).
 */
export async function fetchDecision({ push, data, list, signal }) {
	const query = decisionQuery(push, data)
	if (!query || typeof list !== 'function') return null
	try {
		const body = await list(query)
		if (signal?.aborted) return null
		return matchDecision(body?.items, push)
	} catch {
		return null
	}
}
